import React, { useState, useEffect } from 'react';
import {
  BrainCircuit, Search, ShieldAlert, CheckCircle2,
  AlertTriangle, User, MessageSquare, Clock, Image as ImageIcon,
  Send, X, Ban, Check, ChevronRight, RefreshCw, Zap, GraduationCap
} from 'lucide-react';
import { getLocalAiLogs } from './aiLogService';

// Basit Markdown biçimlendirici
const TranscriptText = ({ content }) => {
  if (!content) return null;
  const lines = content.split('\n');

  return (
    <div className="space-y-1.5 text-xs sm:text-sm leading-relaxed text-slate-200">
      {lines.map((line, idx) => {
        const trimmed = line.trim();

        if (trimmed.startsWith('📌') || trimmed.includes('Kazanım Analizi')) {
          return (
            <div key={idx} className="mt-2.5 mb-1 p-2 rounded-lg bg-orange-500/15 border border-orange-500/30 text-orange-300 font-semibold text-xs flex items-center gap-2">
              <span>📌</span>
              <span>{trimmed.replace(/^📌\s*/, '')}</span>
            </div>
          );
        }

        if (trimmed.startsWith('- **Ders:**') || trimmed.startsWith('- **Ünite:**') || trimmed.startsWith('- **Konu / Kazanım:**')) {
          return (
            <div key={idx} className="ml-2 pl-2.5 py-0.5 border-l-2 border-orange-500/40 text-xs text-slate-300 font-mono">
              <span dangerouslySetInnerHTML={{ __html: formatInline(trimmed.replace(/^-\s*/, '')) }} />
            </div>
          );
        }

        if (trimmed.startsWith('### ')) {
          return (
            <h4 key={idx} className="mt-2.5 mb-1 text-xs font-bold text-orange-400">
              {trimmed.replace(/^###\s*/, '')}
            </h4>
          );
        }

        if (trimmed.startsWith('* ') || trimmed.startsWith('- ')) {
          return (
            <div key={idx} className="flex items-start gap-1.5 ml-1">
              <span className="text-orange-400 mt-1 text-[10px]">•</span>
              <span className="flex-1" dangerouslySetInnerHTML={{ __html: formatInline(trimmed.substring(2)) }} />
            </div>
          );
        }

        if (!trimmed) return <div key={idx} className="h-1" />;

        return (
          <p key={idx} dangerouslySetInnerHTML={{ __html: formatInline(line) }} />
        );
      })}
    </div>
  );
};

function formatInline(text) {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-white">$1</strong>')
    .replace(/\*(.*?)\*/g, '<em class="italic text-slate-300">$1</em>')
    .replace(/\$+/g, '');
}

export default function AiUsageSuperadminModule({
  allStudents = [],
  admins = [],
  user,
  onUpdateStudent,
  db,
  isFirebaseActive
}) {
  const [logs, setLogs] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [accountTypeFilter, setAccountTypeFilter] = useState('all'); // 'all' | 'student' | 'teacher' | 'has_usage' | 'blocked'
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Müdahale Modalı State
  const [interventionModal, setInterventionModal] = useState({
    isOpen: false,
    student: null,
    referencedLog: null,
    message: ''
  });
  const [isSendingIntervention, setIsSendingIntervention] = useState(false);

  // Logları yükle (Firebase + LocalStorage birleşimi)
  const fetchLogs = async () => {
    setIsLoadingLogs(true);
    let allLogs = getLocalAiLogs();

    // allStudents ve admins dokümanlarındaki logları da topla
    (allStudents || []).forEach(s => {
      if (Array.isArray(s.aiInteractions)) {
        allLogs = [...allLogs, ...s.aiInteractions];
      }
    });
    (admins || []).forEach(a => {
      if (Array.isArray(a.aiInteractions)) {
        allLogs = [...allLogs, ...a.aiInteractions];
      }
    });

    if (isFirebaseActive && db) {
      try {
        const { collection, getDocs, query, where, limit } = await import("firebase/firestore");
        const q = query(collection(db, "notifications"), where("isAiLogNotification", "==", true), limit(300));
        const snap = await getDocs(q);
        const fbLogs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        allLogs = [...allLogs, ...fbLogs];
      } catch (err) {
        console.warn("Firestore notifications AI logları okunamadı:", err);
      }
    }

    const map = new Map();
    allLogs.forEach(l => map.set(l.id, l));
    const sorted = Array.from(map.values()).sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
    setLogs(sorted);
    setIsLoadingLogs(false);
  };

  useEffect(() => {
    fetchLogs();
  }, [isFirebaseActive, db, allStudents, admins]);

  // Kullanıcı (öğrenci veya öğretmen) bazında log istatistikleri haritası
  const statsMap = React.useMemo(() => {
    const map = {};

    // 1. Doğrudan Firestore doküman verilerinden (anlık senkronize)
    (allStudents || []).forEach(s => {
      const uId = String(s.id);
      const studentLogs = Array.isArray(s.aiInteractions) ? s.aiInteractions : [];
      const promptCount = (typeof s.aiPromptCount === 'number' && s.aiPromptCount > 0)
        ? s.aiPromptCount
        : studentLogs.length;

      map[uId] = {
        totalPrompts: promptCount,
        imageCount: studentLogs.filter(l => l.hasImage).length,
        blockedCount: studentLogs.filter(l => l.intent?.includes('Engellendi')).length,
        lastLog: studentLogs[studentLogs.length - 1] || null,
        intents: {}
      };
      studentLogs.forEach(l => {
        if (l.intent) map[uId].intents[l.intent] = (map[uId].intents[l.intent] || 0) + 1;
      });
    });

    (admins || []).forEach(a => {
      const uId = String(a.id);
      const teacherLogs = Array.isArray(a.aiInteractions) ? a.aiInteractions : [];
      const promptCount = (typeof a.aiPromptCount === 'number' && a.aiPromptCount > 0)
        ? a.aiPromptCount
        : teacherLogs.length;

      map[uId] = {
        totalPrompts: promptCount,
        imageCount: teacherLogs.filter(l => l.hasImage).length,
        blockedCount: 0,
        lastLog: teacherLogs[teacherLogs.length - 1] || null,
        intents: {}
      };
      teacherLogs.forEach(l => {
        if (l.intent) map[uId].intents[l.intent] = (map[uId].intents[l.intent] || 0) + 1;
      });
    });

    // 2. Ardından global logs havuzu ile tamamla
    logs.forEach(log => {
      const uId = String(log.studentId);
      if (!map[uId]) {
        map[uId] = {
          totalPrompts: 0,
          imageCount: 0,
          blockedCount: 0,
          lastLog: log,
          intents: {}
        };
      }
      map[uId].totalPrompts = Math.max(map[uId].totalPrompts, (map[uId].totalPrompts || 0) + 1);
      if (log.hasImage) map[uId].imageCount++;
      if (log.intent?.includes('Engellendi')) map[uId].blockedCount++;
      map[uId].intents[log.intent] = (map[uId].intents[log.intent] || 0) + 1;
    });

    return map;
  }, [allStudents, admins, logs]);

  // 1. PASİFE/ARŞİVE ALINMAYAN VE KOÇU MEVCUT OLAN AKTİF ÖĞRENCİLER
  const activeStudents = React.useMemo(() => {
    const validAdminIds = new Set(admins.map(a => String(a.id)));
    validAdminIds.add("super_admin_hmk");
    validAdminIds.add("Hamza Metehan Kılıç");
    if (user?.id) validAdminIds.add(String(user.id));

    return allStudents.filter(s => {
      if (s.isArchived || s.status === 'archived') return false;
      // Koçu silinmiş yetim öğrencileri filtrele
      if (s.teacherId && !validAdminIds.has(String(s.teacherId))) return false;
      return true;
    });
  }, [allStudents, admins, user]);

  // 2. TÜM HESAPLARIN (AKTİF ÖĞRENCİLER + ÖĞRETMENLER) BİRLEŞTİRİLMESİ
  const allAccounts = React.useMemo(() => {
    const studentItems = activeStudents.map(s => ({
      id: String(s.id),
      name: s.name,
      role: 'student',
      grade: s.grade || s.class || 'Öğrenci',
      field: s.field || 'Genel',
      teacherId: s.teacherId,
      aiBlocked: !!s.aiBlocked,
      raw: s
    }));

    const teacherItems = (admins || []).map(a => ({
      id: String(a.id),
      name: a.name,
      role: 'teacher',
      grade: 'Öğretmen / Danışman',
      field: a.username || '',
      teacherId: null,
      aiBlocked: false,
      raw: a
    }));

    return [...studentItems, ...teacherItems];
  }, [activeStudents, admins]);

  // 3. EN ÇOK KULLANIMA GÖRE (PROMPT SAYISI DESCENDING) SIRALAMA VE FİLTRELEME
  const sortedAndFilteredAccounts = React.useMemo(() => {
    return allAccounts
      .filter(acc => {
        const stat = statsMap[acc.id];
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          (acc.name || '').toLowerCase().includes(searchLower) ||
          (acc.grade || '').toLowerCase().includes(searchLower);

        if (!matchesSearch) return false;

        if (accountTypeFilter === 'all') return true;
        if (accountTypeFilter === 'student') return acc.role === 'student';
        if (accountTypeFilter === 'teacher') return acc.role === 'teacher';
        if (accountTypeFilter === 'has_usage') return !!stat && stat.totalPrompts > 0;
        if (accountTypeFilter === 'blocked') return !!stat && stat.blockedCount > 0;
        return true;
      })
      .sort((a, b) => {
        // En çok prompt gönderen en üstte
        const countA = statsMap[a.id]?.totalPrompts || 0;
        const countB = statsMap[b.id]?.totalPrompts || 0;
        if (countB !== countA) return countB - countA;
        return (a.name || '').localeCompare(b.name || '', 'tr');
      });
  }, [allAccounts, statsMap, searchTerm, accountTypeFilter]);

  // Seçili hesap (varsayılan: en çok kullanan ilk hesap)
  const selectedAccount =
    allAccounts.find(a => String(a.id) === String(selectedAccountId)) ||
    sortedAndFilteredAccounts[0] ||
    null;

  // Seçili hesabın logları (dokümandan veya global loglardan)
  const selectedAccountLogs = React.useMemo(() => {
    if (!selectedAccount) return [];
    const directLogs = Array.isArray(selectedAccount.raw?.aiInteractions) ? selectedAccount.raw.aiInteractions : [];
    const poolLogs = logs.filter(l => String(l.studentId) === String(selectedAccount.id));
    const map = new Map();
    poolLogs.forEach(l => map.set(l.id, l));
    directLogs.forEach(l => map.set(l.id, l));
    return Array.from(map.values()).sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
  }, [selectedAccount, logs]);

  // Genel Metrikler
  const totalPromptsCount = logs.length;
  const activeUserCount = Object.keys(statsMap).length;
  const totalImagesCount = logs.filter(l => l.hasImage).length;
  const totalBlockedCount = logs.filter(l => l.intent?.includes('Engellendi')).length;

  // AI Erişimini Aç / Kapat (Sadece öğrenciler için)
  const handleToggleAiBlock = async (account) => {
    if (!account || account.role !== 'student') return;
    const studentRaw = account.raw;
    const isCurrentlyBlocked = !!studentRaw.aiBlocked;
    const actionText = isCurrentlyBlocked ? "AI erişimini tekrar açmak" : "AI erişimini duraklatmak";

    const confirmed = window.confirm(`${studentRaw.name} isimli öğrencinin ${actionText} istediğinize emin misiniz?`);
    if (!confirmed) return;

    const updated = {
      ...studentRaw,
      aiBlocked: !isCurrentlyBlocked
    };

    try {
      await onUpdateStudent(updated);
      setToastMessage(`${studentRaw.name} için AI erişimi ${!isCurrentlyBlocked ? 'durduruldu' : 'açıldı'}.`);
      setTimeout(() => setToastMessage(''), 3500);
    } catch (err) {
      alert("Güncelleme hatası: " + err.message);
    }
  };

  // Öğrenciye Müdahale / Düzeltme Bildirimi Gönderme
  const handleSendIntervention = async () => {
    if (!interventionModal.message.trim() || !interventionModal.student) return;

    setIsSendingIntervention(true);
    const target = interventionModal.student;
    const textMsg = `[Koçluk/Yönetici Müdahalesi]: ${interventionModal.message.trim()}`;

    const newNotif = {
      text: textMsg,
      date: new Date().toISOString(),
      sender: "Süperadmin / HMK Koçluk",
      target: target.id,
      readBy: []
    };

    try {
      if (isFirebaseActive && db) {
        const { collection, addDoc } = await import("firebase/firestore");
        await addDoc(collection(db, "notifications"), newNotif);
      }

      setToastMessage(`Düzeltme bildirimi başarıyla ${target.name} adlı kullanıcıya iletildi.`);
      setTimeout(() => setToastMessage(''), 4000);
      setInterventionModal({ isOpen: false, student: null, referencedLog: null, message: '' });
    } catch (e) {
      console.error(e);
      alert("Bildirim gönderilemedi: " + e.message);
    } finally {
      setIsSendingIntervention(false);
    }
  };

  // Bir loga tıklandığında müdahale modalını aç
  const openInterventionForLog = (logItem) => {
    const acc = allAccounts.find(a => String(a.id) === String(logItem.studentId)) || selectedAccount;
    setInterventionModal({
      isOpen: true,
      student: acc,
      referencedLog: logItem,
      message: `Sevgili ${acc?.name || 'öğrencim'}, ${logItem.date} tarihinde yapay zekaya sorduğun soru çözümünde şu noktaya dikkat etmelisin: `
    });
  };

  return (
    <div className="space-y-6 animate-fade-in pb-12 max-w-7xl mx-auto">
      
      {/* BAŞLIK VE GENEL BAKIŞ */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-2xl border border-slate-700/80 p-5 shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-600 to-orange-500 text-white flex items-center justify-center shadow-lg shadow-purple-500/30 shrink-0">
              <BrainCircuit size={26} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg sm:text-xl font-bold text-white tracking-tight">
                  Süperadmin: AI Kullanım & Denetim Paneli
                </h1>
                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30">
                  Canlı Denetim
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                Aktif öğrencilerin ve öğretmenlerin yapay zeka kullanım istatistikleri ve anlık konuşma denetimi
              </p>
            </div>
          </div>

          <button
            onClick={fetchLogs}
            disabled={isLoadingLogs}
            className="px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700 text-xs font-semibold flex items-center gap-2 transition-all self-start md:self-auto"
          >
            <RefreshCw size={14} className={isLoadingLogs ? "animate-spin" : ""} />
            <span>Verileri Yenile</span>
          </button>
        </div>

        {/* METRİK KARTLARI */}
        <div className="mt-5 pt-4 border-t border-slate-700/60 grid grid-cols-2 lg:grid-cols-4 gap-3.5">
          <div className="bg-slate-800/60 p-3.5 rounded-xl border border-slate-700/50">
            <div className="flex items-center justify-between text-slate-400 text-xs mb-1">
              <span>Toplam Soru/Prompt</span>
              <MessageSquare size={16} className="text-orange-400" />
            </div>
            <div className="text-2xl font-black text-white">{totalPromptsCount}</div>
            <span className="text-[10px] text-slate-400">Sistem genelinde girilen istek</span>
          </div>

          <div className="bg-slate-800/60 p-3.5 rounded-xl border border-slate-700/50">
            <div className="flex items-center justify-between text-slate-400 text-xs mb-1">
              <span>AI Kullanan Hesaplar</span>
              <User size={16} className="text-indigo-400" />
            </div>
            <div className="text-2xl font-black text-white">{activeUserCount}</div>
            <span className="text-[10px] text-slate-400">{activeStudents.length} aktif öğrenci + {admins.length} öğretmen</span>
          </div>

          <div className="bg-slate-800/60 p-3.5 rounded-xl border border-slate-700/50">
            <div className="flex items-center justify-between text-slate-400 text-xs mb-1">
              <span>Fotoğraflı Soru Çözümü</span>
              <ImageIcon size={16} className="text-blue-400" />
            </div>
            <div className="text-2xl font-black text-white">{totalImagesCount}</div>
            <span className="text-[10px] text-slate-400">Yüklenen soru görseli</span>
          </div>

          <div className="bg-slate-800/60 p-3.5 rounded-xl border border-slate-700/50">
            <div className="flex items-center justify-between text-slate-400 text-xs mb-1">
              <span>Engellenen Koçluk Talepleri</span>
              <ShieldAlert size={16} className="text-amber-400" />
            </div>
            <div className="text-2xl font-black text-white">{totalBlockedCount}</div>
            <span className="text-[10px] text-slate-400">Koça yönlendirilen program istekleri</span>
          </div>
        </div>
      </div>

      {/* TOAST BİLDİRİMİ */}
      {toastMessage && (
        <div className="bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 px-4 py-2.5 rounded-xl text-xs font-semibold flex items-center justify-between animate-fade-in">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={16} />
            <span>{toastMessage}</span>
          </div>
          <button onClick={() => setToastMessage('')} className="p-1 hover:opacity-80">
            <X size={14} />
          </button>
        </div>
      )}

      {/* İKİ KOLONLU MASTER-DETAIL DÜZENİ */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* SOL KOLON: KULLANICILAR LİSTESİ (PROMPT SAYISINA GÖRE AZALAN SIRALI) */}
        <div className="lg:col-span-5 space-y-3">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 shadow-lg space-y-3">
            
            {/* Arama Kutusu */}
            <div className="relative">
              <Search size={15} className="absolute left-3 top-3 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Öğrenci veya öğretmen ara..."
                className="w-full bg-slate-800 border border-slate-700 rounded-xl pl-9 pr-3 py-2 text-xs text-white placeholder-slate-400 focus:outline-none focus:border-orange-500"
              />
            </div>

            {/* Filtre Sekmeleri */}
            <div className="flex flex-wrap gap-1.5 text-[11px]">
              <button
                onClick={() => setAccountTypeFilter('all')}
                className={`px-2.5 py-1 rounded-lg font-semibold transition-colors ${
                  accountTypeFilter === 'all' ? 'bg-orange-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
              >
                Tümü ({allAccounts.length})
              </button>
              <button
                onClick={() => setAccountTypeFilter('student')}
                className={`px-2.5 py-1 rounded-lg font-semibold transition-colors ${
                  accountTypeFilter === 'student' ? 'bg-orange-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
              >
                Öğrenciler ({activeStudents.length})
              </button>
              <button
                onClick={() => setAccountTypeFilter('teacher')}
                className={`px-2.5 py-1 rounded-lg font-semibold transition-colors ${
                  accountTypeFilter === 'teacher' ? 'bg-purple-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
              >
                Öğretmenler ({admins.length})
              </button>
              <button
                onClick={() => setAccountTypeFilter('has_usage')}
                className={`px-2.5 py-1 rounded-lg font-semibold transition-colors ${
                  accountTypeFilter === 'has_usage' ? 'bg-orange-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
              >
                AI Kullananlar ({activeUserCount})
              </button>
            </div>

            {/* Bilgilendirme Sıralama İpucu */}
            <div className="text-[10px] text-slate-400 px-1 flex items-center justify-between">
              <span>Sıralama: <b>En çok prompt gönderen en üstte</b></span>
              <span>{sortedAndFilteredAccounts.length} kayıt</span>
            </div>

            {/* Hesap Kartları Listesi */}
            <div className="space-y-2 max-h-[580px] overflow-y-auto custom-scrollbar pr-1">
              {sortedAndFilteredAccounts.map((acc, index) => {
                const stat = statsMap[acc.id];
                const promptCount = stat?.totalPrompts || 0;
                const isSelected = selectedAccount && String(selectedAccount.id) === String(acc.id);
                const isTeacher = acc.role === 'teacher';
                const isBlocked = acc.aiBlocked;
                const teacherObj = !isTeacher ? admins.find(a => String(a.id) === String(acc.teacherId)) : null;

                return (
                  <div
                    key={acc.id}
                    onClick={() => setSelectedAccountId(acc.id)}
                    className={`p-3 rounded-xl border text-left cursor-pointer transition-all flex items-center justify-between ${
                      isSelected
                        ? 'bg-slate-800 border-orange-500 shadow-lg ring-1 ring-orange-500/40'
                        : 'bg-slate-800/40 border-slate-700/60 hover:bg-slate-800/80 text-slate-300'
                    }`}
                  >
                    <div className="space-y-1 min-w-0 flex-1">
                      
                      {/* ÜST SATIR: İsim + Kullanım Sayısı Rozeti + Rol/Durum Rozeti */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-xs sm:text-sm text-white truncate max-w-[170px]">
                          {acc.name}
                        </span>

                        {/* KULLANIM SAYISI (PROMPT ADEDİ) */}
                        <span className={`text-[11px] px-2 py-0.5 rounded-md font-bold shrink-0 flex items-center gap-1 ${
                          promptCount > 0
                            ? 'bg-orange-500/20 text-orange-400 border border-orange-500/40'
                            : 'bg-slate-800 text-slate-400 border border-slate-700'
                        }`}>
                          {promptCount > 0 && <Zap size={10} className="fill-orange-400" />}
                          <span>{promptCount} Prompt</span>
                        </span>

                        {/* ROL / DURUM ROZETİ */}
                        {isTeacher ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/35 font-bold shrink-0">
                            Öğretmen
                          </span>
                        ) : isBlocked ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400 border border-red-500/30 font-semibold shrink-0">
                            AI Kısıtlı
                          </span>
                        ) : (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-semibold shrink-0">
                            Aktif
                          </span>
                        )}
                      </div>

                      {/* ALT SATIR: Sınıf / Koç Bilgisi ve Son Kullanım */}
                      <div className="text-[11px] text-slate-400 flex items-center gap-2 truncate">
                        <span>{acc.grade}</span>
                        {!isTeacher && (
                          <>
                            <span>•</span>
                            <span className={teacherObj ? "text-slate-300 font-medium" : "text-amber-400/90 font-medium"}>
                              Koç: {teacherObj?.name || (acc.teacherId === 'super_admin_hmk' || acc.teacherId === 'Hamza Metehan Kılıç' || acc.teacherId === user?.id ? 'Hamza Metehan Kılıç' : 'Atanmamış')}
                            </span>
                          </>
                        )}
                      </div>

                      {stat && stat.lastLog && (
                        <div className="flex items-center gap-3 text-[10px] text-slate-400 pt-0.5">
                          {stat.imageCount > 0 && <span>📸 {stat.imageCount} Görsel</span>}
                          <span>Son Kullanım: {stat.lastLog.date} {stat.lastLog.time}</span>
                        </div>
                      )}
                    </div>

                    <ChevronRight size={16} className={`shrink-0 ml-2 ${isSelected ? 'text-orange-500' : 'text-slate-500'}`} />
                  </div>
                );
              })}

              {sortedAndFilteredAccounts.length === 0 && (
                <div className="text-center py-8 text-xs text-slate-400">
                  Aramaya uygun aktif öğrenci veya öğretmen bulunamadı.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* SAĞ KOLON: SEÇİLİ KULLANICININ AI GEÇMİŞİ VE MÜDAHALE PANELİ */}
        <div className="lg:col-span-7 space-y-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 shadow-lg flex flex-col h-full min-h-[640px]">
            
            {/* Üst Bar: Seçili Kullanıcı Bilgisi ve Müdahale Aksiyonları */}
            {selectedAccount ? (
              <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-slate-800">
                <div>
                  <h3 className="font-bold text-white text-sm sm:text-base flex items-center gap-2">
                    {selectedAccount.role === 'teacher' ? (
                      <GraduationCap size={18} className="text-purple-400" />
                    ) : (
                      <User size={18} className="text-orange-400" />
                    )}
                    <span>{selectedAccount.name}</span>

                    {selectedAccount.role === 'teacher' ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/35 font-bold">
                        Öğretmen
                      </span>
                    ) : (
                      <span className="text-xs font-normal text-slate-400">
                        ({selectedAccount.grade} / {selectedAccount.field})
                      </span>
                    )}
                  </h3>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Bu kullanıcının sistemde kayıtlı toplam <b>{selectedAccountLogs.length}</b> adet yapay zeka promptu / etkileşimi bulunmaktadır.
                  </p>
                </div>

                {/* Kontrol Butonları (Sadece Öğrenciler İçin AI Duraklatma ve Düzeltme) */}
                {selectedAccount.role === 'student' && (
                  <div className="flex items-center gap-2">
                    {/* AI Erişimini Aç / Kapat Butonu */}
                    <button
                      type="button"
                      onClick={() => handleToggleAiBlock(selectedAccount)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all border ${
                        selectedAccount.aiBlocked
                          ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/25'
                          : 'bg-red-500/15 border-red-500/40 text-red-400 hover:bg-red-500/25'
                      }`}
                      title={selectedAccount.aiBlocked ? "Öğrencinin AI erişimini tekrar aç" : "Öğrencinin AI erişimini geçici olarak kapat"}
                    >
                      {selectedAccount.aiBlocked ? (
                        <>
                          <Check size={14} />
                          <span>Erişimi Aç</span>
                        </>
                      ) : (
                        <>
                          <Ban size={14} />
                          <span>AI'ı Duraklat</span>
                        </>
                      )}
                    </button>

                    {/* Doğrudan Müdahale Bildirimi Gönder Butonu */}
                    <button
                      type="button"
                      onClick={() => {
                        setInterventionModal({
                          isOpen: true,
                          student: selectedAccount,
                          referencedLog: null,
                          message: `Sevgili ${selectedAccount.name}, yapay zeka çalışmalarınla ilgili koçluk notu: `
                        });
                      }}
                      className="px-3 py-1.5 rounded-xl bg-orange-600 hover:bg-orange-500 text-white text-xs font-bold flex items-center gap-1.5 shadow-md shadow-orange-500/20 transition-all"
                    >
                      <AlertTriangle size={14} />
                      <span>Öğrenciye Bildirim/Düzeltme İlet</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-6 text-xs text-slate-400">
                İncelemek için sol taraftan bir kullanıcı seçiniz.
              </div>
            )}

            {/* Konuşma Geçmişi (Chat Transcript) */}
            <div className="flex-1 overflow-y-auto py-4 space-y-4 custom-scrollbar max-h-[540px]">
              {selectedAccountLogs.length === 0 ? (
                <div className="text-center py-16 space-y-2 text-slate-400">
                  <MessageSquare size={32} className="mx-auto text-slate-600" />
                  <p className="text-xs">Bu kullanıcı henüz yapay zekaya bir soru sormamış veya konuşma yapmamış.</p>
                </div>
              ) : (
                selectedAccountLogs.map((logItem, idx) => (
                  <div
                    key={logItem.id || idx}
                    className="p-4 rounded-2xl bg-slate-950/70 border border-slate-800 space-y-3 relative group hover:border-slate-700 transition-colors"
                  >
                    {/* Log Üst Barı: Amaç & Tarih */}
                    <div className="flex items-center justify-between text-xs pb-2 border-b border-slate-800/80">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          logItem.intent?.includes('Engellendi')
                            ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                            : logItem.intent?.includes('Öğretmen')
                            ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                            : logItem.intent === 'Soru Çözümü'
                            ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                            : logItem.intent === 'Kazanım & Konu Tespiti'
                            ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                            : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                        }`}>
                          {logItem.intent}
                        </span>

                        {logItem.hasImage && (
                          <span className="text-[10px] text-blue-400 flex items-center gap-1">
                            <ImageIcon size={12} /> Görsel Yüklendi
                          </span>
                        )}
                      </div>

                      <div className="text-[11px] text-slate-400 flex items-center gap-1.5">
                        <Clock size={12} />
                        <span>{logItem.date} {logItem.time}</span>
                      </div>
                    </div>

                    {/* Kullanıcının Sorusu / İstemi */}
                    <div className="space-y-1">
                      <span className="text-[11px] font-bold text-orange-400 uppercase tracking-wider block">
                        {selectedAccount?.role === 'teacher' ? 'Öğretmenin İstemi / Sorusu:' : 'Öğrencinin Sorusu / İstemi:'}
                      </span>
                      <p className="text-xs sm:text-sm text-white font-medium whitespace-pre-wrap bg-slate-900/90 p-2.5 rounded-xl border border-slate-800">
                        {logItem.userPrompt}
                      </p>
                    </div>

                    {/* Yapay Zekanın Yanıtı */}
                    <div className="space-y-1">
                      <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                        Koçum Online AI Yanıtı:
                      </span>
                      <div className="bg-slate-900/50 p-3 rounded-xl border border-slate-800/80 text-xs text-slate-200">
                        <TranscriptText content={logItem.aiResponse} />
                      </div>
                    </div>

                    {/* Müdahale Butonu (Sadece öğrenciler için) */}
                    {selectedAccount?.role === 'student' && (
                      <div className="pt-1 flex justify-end">
                        <button
                          type="button"
                          onClick={() => openInterventionForLog(logItem)}
                          className="px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-amber-300 text-xs font-semibold flex items-center gap-1.5 transition-colors border border-amber-500/20"
                          title="Bu yanıtta bir hata varsa öğrenciye anında düzeltme bildirimi ilet"
                        >
                          <AlertTriangle size={13} />
                          <span>Bu Yanıta Müdahale Et / Düzeltme İlet</span>
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* MÜDAHALE & DÜZELTME BİLDİRİMİ MODALI */}
      {interventionModal.isOpen && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700/90 rounded-2xl p-6 max-w-lg w-full shadow-2xl space-y-4">
            
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <div className="flex items-center gap-2">
                <AlertTriangle size={18} className="text-amber-400" />
                <h3 className="text-sm font-bold text-white">Öğrenciye Doğrudan Düzeltme & Müdahale</h3>
              </div>
              <button
                type="button"
                onClick={() => setInterventionModal({ isOpen: false, student: null, referencedLog: null, message: '' })}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-1 text-xs text-slate-300">
              <p>
                <b>Hedef Öğrenci:</b> <span className="text-orange-400 font-bold">{interventionModal.student?.name}</span>
              </p>
              <p className="text-[11px] text-slate-400">
                Bu mesaj doğrudan öğrencinin bildirim paneline <b>"Koçluk/Yönetici Müdahalesi"</b> olarak anında düşecektir.
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-300">Müdahale / Düzeltme Notunuz:</label>
              <textarea
                value={interventionModal.message}
                onChange={(e) => setInterventionModal({ ...interventionModal, message: e.target.value })}
                rows={5}
                placeholder="Örn: Yapay zekanın çözdüğü soruda 3. adımdaki Pisagor teoreminde hipotenüs yerine dik kenar alınmış, formülün doğrusu a² + b² = c² şeklindedir..."
                className="w-full bg-slate-950 border border-slate-700 rounded-xl p-3 text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-orange-500 resize-none custom-scrollbar"
              />
            </div>

            <div className="flex items-center justify-end gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => setInterventionModal({ isOpen: false, student: null, referencedLog: null, message: '' })}
                className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold"
              >
                Vazgeç
              </button>

              <button
                type="button"
                onClick={handleSendIntervention}
                disabled={isSendingIntervention || !interventionModal.message.trim()}
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-500 hover:to-amber-500 disabled:opacity-40 text-white text-xs font-bold flex items-center gap-1.5 shadow-md shadow-orange-500/20"
              >
                {isSendingIntervention ? (
                  <span>İletiliyor...</span>
                ) : (
                  <>
                    <Send size={14} />
                    <span>Öğrenciye Bildirimi İlet</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
